import * as React from 'react';
import {HelmetProvider, HelmetServerState} from 'react-helmet-async';
import {SliceZone} from '@prismicio/react';
import {useEffect, useMemo, useState} from 'react';

import Button from '../../ui/Buttons/Button';
import {BrowseSidebar} from '../browse-sidebar/BrowseSidebar';
import {CompareItems} from '../../items/compare/CompareItems';
import {CompareModals} from '../../items/compare/CompareFunctions';
import {ContentBlock} from '../../cms/content-block/ContentBlock';
import {EmptyState} from '../../ui/EmptyState/EmptyState';
import {FaqSection} from '../../cms/faq/FaqSection';
import {FeaturedProduct} from '../featured-product/FeaturedProduct';
import {gaLegacySiteSearch} from '../../../client/ga/ga-legacy.functions';
import {GuidedNav} from '../guided-nav/GuidedNav';
import {ImpUrl} from '../../../client/imp-url/imp-url.class';
import {LocalStorageService} from '../../../client/local-storage/local-storage.service';
import {MonetateService} from '../../../client/monetate/monetate.service';
import {MROResult} from '../../header/search-overlay/MROResult';
import {NullResults} from './NullResults';
import {PageHeader} from '../../ui/PageHeader/PageHeader';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {ResultPane} from '../result-pane/ResultPane';
import {SearchResult} from '../search.types';
import {SelectorFacets} from './SelectorFacets';
import {SerializedURLBuilder, URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {StaticData, VehicleSelector} from '../vehicle-selector/VehicleSelector';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {ModalBackdrop} from '../../ui/modals/ModalBackdrop';
import {signal} from '@preact/signals-react';

export const showSidebar = signal(false);

export interface SearchPageProps {
    debugWebcat: boolean;
    gaCategoryPath: string;
    helmetContext: {helmet?: HelmetServerState};
    isPrevPurchasedView: boolean;
    resultList?: SearchResult;
    serializedURLBuilder: SerializedURLBuilder;
    staticData: StaticData;
    stepIdPath: string;
    userData: User;
}

export const SearchPage = ({
    debugWebcat,
    gaCategoryPath,
    helmetContext,
    isPrevPurchasedView,
    resultList,
    serializedURLBuilder,
    staticData,
    stepIdPath,
    userData,
}: SearchPageProps) => {
    const [isClient, setIsClient] = useState(false);
    const buildURL = useMemo(() => URLBuilder.fromSerialized(serializedURLBuilder), [serializedURLBuilder]);
    const localStorageService: LocalStorageService = useService(`localStorageService`);
    const monetateService: MonetateService = useService(`monetateService`);
    const user = useMemo(() => new User(userData), [userData]);
    // TBD
    useEffect(() => {
        setIsClient(true);
    }, []);

    // TBD
    useEffect(() => {
        // Clear GA searchTerm if visiting a root level category page
        if (localStorageService.storageAvailable) {
            if (resultList.layout === `cat` && resultList.webcatLevel === `webcatid0`) {
                localStorageService.removeItem(`searchTerm`);
            }
        }

        // Set monetate pageType
        monetateService.setPageType(ImpUrl.getUrlSegment(1));

        // Record custom GA event related to searchTerm (from query param)
        const searchTerm = ImpUrl.getQueryParamVal(`SearchString`) || ImpUrl.getQueryParamVal(`redirectKeyword`);
        const gaEvent = ImpUrl.getQueryParamVal(`SearchString`) ? `ga4_view_search_results` : `ga4_view_redirected_search_results`;
        if (searchTerm) {
            gaLegacySiteSearch(searchTerm, `legacy_search`, resultList.results?.length > 0 || resultList.subCats?.length > 0);
            pushGaEvent(gaEvent, {
                had_search_results: resultList.results?.length > 0,
                search_term: searchTerm,
            });
        }

        // Only run once per page view
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * TBD
     */
    const showHeader =
        !(
            resultList.layout === `campaign` ||
            resultList.layout === `collection` ||
            (resultList.categorySuggestions && resultList.categorySuggestions.length > 0)
        ) || resultList.totalItems === 0;
    if (resultList.mroMatch) {
        return (
            <div className="tw-flex">
                <MROResult mroMatch={resultList.mroMatch} />
            </div>
        );
    }
    const onClose = () => {
        showSidebar.value = false;
    };

    /**
     * Template
     */
    return (
        <HelmetProvider context={helmetContext}>
            <div>
                <div className="tw-flex">
                    <div
                        className={`tw-w-1/4 ${showSidebar.value ? '!tw-w-auto tw-block' : 'tw-hidden '} ${
                            resultList.layout !== 'cat' && resultList.totalItems > 0 ? 'lg:tw-flex' : 'tw-flex'
                        } tw-pr-3 position-relative`}
                    >
                        {showSidebar.value && (
                            <div className={`tw-block${resultList.layout === 'cat' ? '' : ' lg:tw-hidden'}`}>
                                <ModalBackdrop
                                    containerClass={`!tw-z-[1025]`}
                                    onClick={onClose}
                                />
                            </div>
                        )}
                        <BrowseSidebar
                            onClose={onClose}
                            resultList={resultList}
                            serializedURLBuilder={serializedURLBuilder}
                            user={user}
                        />
                    </div>
                    <div className={`tw-w-full${resultList.layout !== 'cat' && resultList.totalItems > 0 ? ' lg:tw-w-3/4' : ''}`}>
                        <div
                            id="searchContentResults"
                            data-e2e="searchResults"
                        >
                            {resultList.featuredProduct && (
                                <FeaturedProduct
                                    featuredProduct={resultList.featuredProduct}
                                    fusionQueryId={resultList.fusionQueryId}
                                    requestID={resultList.request_id}
                                    user={user}
                                />
                            )}
                            {resultList.specialsBanner && resultList.specialsBanner.url && (
                                <div className="tw-mb-6">
                                    <img
                                        alt="Specials Banner"
                                        className="tw-max-w-full tw-h-auto"
                                        src={resultList.specialsBanner.url}
                                    />
                                </div>
                            )}
                            {resultList.webcatMeta.cmsContent &&
                                resultList.webcatMeta.cmsContent?.responsive_banner_image?.small?.url &&
                                resultList.layout !== `campaign` &&
                                resultList.layout !== `wiper-blades` &&
                                !resultList.bannerAsset && (
                                    <div className="tw-mb-6">
                                        <a href={resultList.webcatMeta.cmsContent.url}>
                                            <picture>
                                                <source
                                                    media="(max-width: 480px)"
                                                    srcSet={
                                                        resultList.webcatMeta.cmsContent.responsive_banner_image.small?.url ||
                                                        resultList.webcatMeta.cmsContent.responsive_banner_image.url
                                                    }
                                                >
                                                    <img
                                                        alt={resultList.webcatMeta.cmsContent.responsive_banner_image.alt}
                                                        className="tw-w-full tw-max-w-full tw-h-auto"
                                                        height={resultList.webcatMeta.cmsContent.responsive_banner_image.dimensions.height}
                                                        src={resultList.webcatMeta.cmsContent.responsive_banner_image.url}
                                                    />
                                                </source>
                                            </picture>
                                        </a>
                                    </div>
                                )}
                            {resultList.bannerAsset?.responsive_banner_image?.small?.url && (
                                <div className="tw-mb-4">
                                    <picture>
                                        <source
                                            media="(max-width: 480px)"
                                            srcSet={
                                                resultList.bannerAsset.responsive_banner_image.small?.url ||
                                                resultList.bannerAsset.responsive_banner_image.url
                                            }
                                        />
                                        <source
                                            media="(max-width: 1024px)"
                                            srcSet={
                                                resultList.bannerAsset.responsive_banner_image.medium?.url ||
                                                resultList.bannerAsset.responsive_banner_image.url
                                            }
                                        />
                                        <img
                                            alt={resultList.bannerAsset.responsive_banner_image.alt}
                                            className="tw-w-full tw-max-w-full tw-h-auto"
                                            height={resultList.bannerAsset.responsive_banner_image.dimensions.height}
                                            src={resultList.bannerAsset.responsive_banner_image.url}
                                            width={resultList.bannerAsset.responsive_banner_image.dimensions.width}
                                        />
                                    </picture>
                                </div>
                            )}
                            {resultList.webcatMeta.categoryPath === `vehicle-trailer-parts/wiper-blades-and-accessories/wiper-blades` && (
                                <VehicleSelector staticData={staticData} />
                            )}
                            {showHeader && (
                                <>
                                    {(resultList.SearchString || !(resultList.guidedNavFacets || resultList.jumpToLinks)) && (
                                        <h1 className="tw-mb-4 tw-flex">
                                            {resultList.SearchString ? (
                                                resultList.totalItems === 0 ? (
                                                    <div>
                                                        0 results found for{' '}
                                                        <div className="tw-text-red-500 tw-inline">'{resultList.SearchString}'</div>
                                                    </div>
                                                ) : (
                                                    <>Search results for '{resultList.SearchString}'</>
                                                )
                                            ) : (
                                                <div dangerouslySetInnerHTML={{__html: resultList.title}}></div>
                                            )}
                                            {debugWebcat && `| WID: ${resultList.webcatMeta.id}`}
                                            {resultList.debug && `| ID: ${resultList.webcatMeta.stepID}`}
                                        </h1>
                                    )}
                                    {(resultList.guidedNavFacets || resultList.jumpToLinks) && resultList.layout === `items` && (
                                        <PageHeader
                                            title={resultList.title}
                                            type="h1"
                                        />
                                    )}
                                    {resultList.brand?.content && <p className="tw-mb-4">{resultList.brand.content}</p>}
                                    {resultList.webcatMeta.content ? (
                                        <div className="tw-flex tw-flex-wrap -tw-mx-2.5">
                                            <div
                                                className={`tw-mb-4 tw-w-full tw-relative tw-px-2.5 ${
                                                    resultList.layout !== 'cat' ? 'tw-max-w-full' : 'tw-max-w-full md:tw-max-w-[58.333333%]'
                                                }`}
                                            >
                                                {resultList.webcatMeta.content}
                                            </div>
                                            <div className="tw-mb-6 tw-w-full tw-relative tw-px-2.5 tw-max-w-full md:tw-max-w-[25%] tw-ml-0 md:tw-ml-[16.6666667%]">
                                                <div className={resultList.layout === `cat` ? `lg:tw-block` : `tw-hidden`}>
                                                    <Button
                                                        block
                                                        className="tw-w-full"
                                                        onClick={() => (showSidebar.value = true)}
                                                        size="lg"
                                                        variant="secondary"
                                                    >
                                                        Filter
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className={`tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-hidden${
                                                resultList.layout === 'cat' ? ' lg:tw-grid' : ''
                                            }`}
                                        >
                                            <div className="tw-mb-6 tw-col-span-1 tw-col-start-1 md:tw-col-start-4">
                                                <Button
                                                    block
                                                    className="tw-w-auto"
                                                    onClick={() => (showSidebar.value = true)}
                                                    size="lg"
                                                    variant="secondary"
                                                >
                                                    Filter
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {isPrevPurchasedView &&
                                        ((resultList.categorySuggestions && resultList.categorySuggestions.length > 0) ||
                                            resultList.totalItems === 0) && (
                                            <EmptyState message="You do not have any previously purchased products" />
                                        )}
                                    {(resultList.guidedNavFacets || resultList.jumpToLinks) && resultList.layout === `items` && (
                                        <div className="tw-mb-4">
                                            <GuidedNav
                                                gaCategoryPath={gaCategoryPath}
                                                resultList={resultList}
                                                serializedURLBuilder={serializedURLBuilder}
                                            />
                                        </div>
                                    )}
                                    <SelectorFacets
                                        buildURL={buildURL}
                                        resultList={resultList}
                                        selectorFacets={resultList.selectorFacets}
                                    />
                                </>
                            )}
                            <ResultPane
                                debugWebcat={debugWebcat}
                                resultList={resultList}
                                serializedURLBuilder={serializedURLBuilder}
                                stepIdPath={stepIdPath}
                                user={user}
                            />
                            {resultList.totalItems === 0 && (
                                <NullResults
                                    resultList={resultList}
                                    user={user}
                                />
                            )}
                            {resultList.cmsContent?.body?.length && (
                                <SliceZone
                                    components={{content_block: ContentBlock, faq_section: FaqSection}}
                                    slices={resultList.cmsContent.body}
                                />
                            )}
                        </div>
                    </div>
                    {isClient && <CompareItems user={user} />}
                </div>
                <CompareModals />
            </div>
        </HelmetProvider>
    );
};
